import { Link } from 'react-router'

import SharedLegalLayout from './layout'

/**
 * TermsOfUse
 *
 * The Terms and Conditions page
 */
const TermsOfUse = () => (
  <SharedLegalLayout heading="Terms and Use">
    <section>
      <p>
        These Terms of Use (&quot;Terms&quot;) govern your access to and use of the services
        provided through the mobile application, Xverse, and the website located at{' '}
        <a href="https://rescuextraining.com" target="_blank" rel="noopener noreferrer">
          <u>rescuextraining.com</u>
        </a>{' '}
        (collectively the &quot;Application&quot;), and any materials, services, information, and
        data hosted or transmitted via the Application, including all Content and User Content, as
        defined below, (collectively, &quot;Services&quot;) as provided by Rescue X Training and
        Consulting, LLC (&quot;us&quot;, &quot;our&quot;, or &quot;we&quot;). The Application may
        be hosted and sold by a third party (&quot;Distributor&quot;), but these Terms are solely
        between you, the user, and Rescue X Training and Consulting, LLC. Your access to and use of
        the Application and Content are conditioned on your acceptance of and compliance with these
        Terms of Use (&quot;Terms&quot;). Please read these Terms carefully.
      </p>
    </section>

    {/* 1. Terms */}
    <section>
      <h2>1. Terms</h2>
      <ol className="list-[lower-alpha]">
        <li>
          By accessing or using the Application you agree to be bound by these Terms. You will use
          the Application in a manner consistent with applicable laws and regulations and in
          accordance with these Terms. If you are dissatisfied with the Application, Services, or
          these Terms, you agree that your sole and exclusive remedy is to discontinue your use of
          the Application. The Application may only be downloaded and installed by an adult 18
          years of age or older and may only be used by a minor if such use is supervised by the
          minor&apos;s parent or guardian.
        </li>
        <li>
          You acknowledge and accept that your use of the Application is at your sole risk. You
          represent you have the legal capacity and authority to accept these Terms and you
          understand that some of these Terms may not apply to you but all the Terms are
          nonetheless binding. As the rightful owner of the Application, we reserve the right to
          change or terminate these Terms at any time and from time to time without any notice to
          you. Any such changes are automatically made part of these Terms and are fully
          enforceable on you.
        </li>
      </ol>
    </section>

    {/* 2. License */}
    <section>
      <h2>2. License</h2>
      <p>
        We grant you a revocable, non-exclusive, non-transferable, limited license to download,
        install, and use the Application solely for your personal, non-commercial purposes strictly
        in accordance with these Terms, and on such devices owned and controlled by you.
      </p>
    </section>

    {/* 3. Ownership */}
    <section>
      <h2>3. Ownership</h2>
      <p>
        The Application and associated Content, is the owned or appropriately licensed property of
        Rescue X Training and Consulting, LLC. The Content is protected by copyrights, trademarks,
        service marks, trade secrets, and/or other proprietary rights and laws, including all
        intellectual property rights whether or not those rights are registered. We reserve all
        applicable rights in and to the Application and Content. Systematic retrieval of data or
        other content from the Application to create or compile, directly or indirectly, a
        collection, compilation, recreation, database, or directory of the Application materials is
        prohibited except as provided for herein. Nothing contained in the Application should be
        construed as granting, by implication, estoppel, or otherwise, any license or right to use
        any intellectual property contained or displayed in the Application without our written
        permission or such third party that may own such intellectual property. Your use of the
        intellectual property contained in the Application, or any other content in the
        Application, except as provided herein, is strictly prohibited. Any unauthorized use of the
        content contained in the Application may violate applicable laws, regulations, and/or
        statutes.
      </p>
    </section>

    {/* 4. Account and Account Security */}
    <section>
      <h2>4. Account and Account Security</h2>
      <p>
        To use certain features of our Application, you may be required to create a user account
        (&quot;Account&quot;) and provide us with a username, password, and certain other
        information about yourself as set forth in our Privacy Policy. You will have the ability to
        maintain and periodically update such information as you see fit. By registering, you agree
        that all information provided by you is true and accurate and that you will maintain and
        update this information as required in order to keep it current, complete, and accurate.
        You are solely responsible for the information associated with your Account and anything
        that happens related to your Account. If you create an Account, you agree that you are
        responsible for maintaining the security and confidentiality of your password and that you
        are fully responsible for all activities and/or charges that are incurred under your
        Account. Therefore, you must take reasonable steps to ensure that others do not gain access
        to your password and Account. You will immediately notify us if you discover or suspect
        that someone has accessed your Account without your permission. You will not license, sell,
        or transfer your Account without our prior written approval.
      </p>

      <h3>SMS Messaging for Multi-Factor Authentication</h3>
      <ol>
        <li>You can opt-in to SMS messaging for MFA verification codes in your Profile Page.</li>
        <li>
          You can cancel the SMS service at any time. Just text &quot;STOP&quot; to the short code.
          After you send the SMS message &quot;STOP&quot; to us, we will send you an SMS message to
          confirm that you have been unsubscribed. After this, you will no longer receive SMS
          messages from us. If you want to join again, just sign up as you did the first time and
          we will start sending SMS messages to you again.
        </li>
        <li>
          If you are experiencing issues with the messaging program you can reply with the keyword
          HELP for more assistance.
        </li>
        <li>Carriers are not liable for delayed or undelivered messages.</li>
        <li>
          As always, message and data rates may apply for any messages sent to you from us and to
          us from you. If you have any questions about your text plan or data plan, it is best to
          contact your wireless provider.
        </li>
        <li>
          If you have any questions regarding privacy, please read our{' '}
          <Link className="underline" to="/privacy-policy" reloadDocument>
            privacy policy
          </Link>
          .
        </li>
      </ol>
    </section>

    {/* 5. User Content */}
    <section>
      <h2>5. User Content</h2>
      <p>
        <b></b>For purposes of this Agreement: (1) the term &quot;Content&quot; means, without
        limitation, video, audio, photographs, images, illustrations, animations, logos, tools,
        information, data, text, software, scripts, executable files, graphics, and interactive
        features, any of which may be generated, provided, or otherwise made accessible on or
        through the Application; (2) the term &quot;User Content&quot; means Content that a user
        submits, transfers, or otherwise provides to the Application, which may be accessible and
        viewable by the public.
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          Posting, transmitting, or sharing User Content through the Application that you did not
          create, that you do not have the rights to, or that you do not have permission to post is
          prohibited. You understand and agree that we may, but are not obligated to, review and
          may delete or remove (without notice) any User Content in our sole discretion, for any
          reason or no reason, including User Content that in our sole judgment violates these
          Terms or which might be offensive or illegal or that might violate the rights, harm, or
          threaten the safety of users or others. You are solely responsible at your sole cost and
          expense for creating backup copies and replacing any User Content you post or store on
          the Application.
        </li>
        <li>
          We claim no ownership to your User Content, however, by posting User Content through any
          part of the Application, you automatically grant, and you represent and warrant that you
          have the right to grant, us an irrevocable, perpetual, non-exclusive, transferable, fully
          paid, worldwide license (with the right to sublicense) to use, copy, publicly perform,
          publicly display, reformat, translate, excerpt (in whole or in part), and distribute such
          User Content for any purpose, on or in connection with the Application or the promotion
          thereof, to prepare derivative works of, or incorporate into other works, such User
          Content, and to grant and authorize sublicenses of the foregoing.
        </li>
        <li>
          You represent and warrant that your User Content conforms to these Terms and that you own
          or have the necessary rights and permissions to use and exploit, and to authorize us to
          use and exploit, your User Content in all manners contemplated by these Terms. You agree
          to indemnify and hold us harmless from any demands, loss, liability, claims, and expenses
          made against us by any third party arising out of or in connection with our use and
          exploitation of your User Content.
        </li>
        <li>
          You can delete User Content individually or all at once by deleting your account. When
          you delete User Content, it’s no longer visible on the site and the license granted
          herein will continue until the User Content has been fully deleted.
        </li>
        <li>
          You represent and warrant that all certification, licenses, and other requested
          documentation uploaded to the Application, as requested by your facility or
          administrators, will be up to date, legitimate and verifiable, and issued by an
          authorized party.
        </li>
        <li>
          You acknowledge and agree that we are in no way responsible for the materials you may
          post to the Application. You agree not to post any confidential information other than
          your own, and at no time will you upload any protected health information.
        </li>
      </ol>
    </section>

    {/* 6. Maintenance */}
    <section>
      <h2>6. Maintenance</h2>
      <p>
        To the extent that any maintenance or support is required by applicable law, we may provide
        support for the Application. Neither we nor Distributor have any obligation to furnish any
        maintenance and support services with respect to the Application.
      </p>
    </section>

    {/* 7. Prohibitions */}
    <section>
      <h2>7. Prohibitions</h2>
      <p>
        You may not: (a) reverse engineer, decompile, tamper with, or create derivative works of
        the Application or Content; (b) change, modify, or alter the Application or Content; (c)
        circumvent any protections that are a part of the Application; (d) use the Application for
        any commercial or illegal purpose, including gambling; (e) collect any information about
        any other user of the Application through the Application; (f) use the Application to
        violate any legal right of any third party; (g) use any automated means to access or use
        the Application (including scripts, &quot;bots&quot; or similar software); (h) duplicate,
        reproduce, publish, display, distribute, or create derivative works of the Content through
        any means, except as provided for herein; or (i) rent, lease, sell, assign, transfer,
        sub-license, or convey the Application or any portion thereof for any purpose.
      </p>
    </section>

    {/* 8. Use */}
    <section>
      <h2>8. Use</h2>
      <p>
        The Application, and all Services, are protected by applicable law, including copyright
        law, and you are explicitly advised that any use of the Application in violation of the law
        is prohibited. You may not use any aspect of the Content and Services for any commercial
        purpose. The Application and the Content may not be modified, duplicated, reproduced,
        transmitted, sold, or otherwise exploited for commercial use. Any unauthorized duplication,
        reproduction, performance, display, distribution, or derivative works of any copyrighted
        Content not owned, or duly licensed, by you is against the law. Use of the Application or
        Services for any purpose not expressly provided for herein is prohibited.
      </p>
    </section>

    {/* 9. User Conduct */}
    <section>
      <h2>9. User Conduct</h2>
      <p>
        You represent, warrant, and agree that no materials of any kind submitted or otherwise
        created, used, posted, transmitted, or shared by you or others through you on or through
        the Application will violate or infringe upon the rights of any third party, including
        copyright, trademark, privacy, publicity, or other personal or proprietary rights, or
        contain libelous, defamatory, or otherwise unlawful material. You further agree not to use
        the Application:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          To take any unlawful or unauthorized actions or in any way damage, disable, overburden,
          or impair the Application or the intellectual property rights owned or licensed by us as
          described elsewhere herein;
        </li>
        <li>
          To upload, post, transmit, share, or otherwise make available any material that contains
          software viruses or any other computer code, files, or programs designed to interrupt,
          destroy, or limit the functionality of any computer software or hardware or
          telecommunications equipment;
        </li>
        <li>
          To interfere with or disrupt the Application or servers or networks connected to the
          Application, or disobey any requirements, procedures, policies, or regulations of
          networks connected to the Application;
        </li>
        <li>
          To use any electronic communication feature of the Application for any purpose that is
          unlawful, tortious, abusive, intrusive on another’s privacy, harassing, libelous,
          defamatory, embarrassing, obscene, threatening, or hateful; or
        </li>
        <li>
          For any commercial purpose not expressly approved by us in writing. You will not use the
          Application to upload, post, email, or otherwise transmit any advertising or promotional
          materials, including without limitation, &quot;junk mail,&quot; &quot;surveys,&quot;
          &quot;spam,&quot; &quot;chain letters,&quot; &quot;pyramid schemes,&quot; or any other
          form of solicitation or unauthorized communication.
        </li>
      </ol>
    </section>

    {/* 10. Charges and Payments */}
    <section>
      <h2>10. Charges and Payments</h2>
      <p>
        We may charge for the Application and your provider’s rates may apply to your download and
        use of the Application. For any payments due for your use of the Application, you authorize
        us to debit your account and accept any payment due.
      </p>
    </section>

    {/* 11. Administrators */}
    <section>
      <h2>11. Administrators</h2>
      <p>
        We may, at our discretion, grant certain Users administrative status within the
        Application. Administrators will have the ability to post User Content, post announcements
        and communications, and moderate and control Users and certain aspects of the Application.
        We are not responsible for actions taken by such administrators. We reserve the right to
        revoke or limit an administrator’s controls at any time and for any reason or no reason,
        including for a breach of these Terms. By agreeing to be an administrator you agree to the
        following:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          You agree that when you receive reports related to the Users or facilities you control
          and moderate, you will take appropriate action, which may include removing content that
          violates these Terms, and/or promptly escalating to us for review;
        </li>
        <li>
          You are not, and may not represent that you are, authorized to act on behalf of us;
        </li>
        <li>
          You may not enter into any agreement with a third party on behalf of us, or any Users or
          facilities that you control and moderate, without our written approval;
        </li>
        <li>
          You may not perform administrative actions, including but not limited to, approval of
          certifications, assignments, and posting of materials in return for any form of
          compensation, consideration, gift, or favor from third parties;
        </li>
        <li>
          If you have access to non-public information as a result of your position as an
          administrator, you will use such information only in connection with your performance as
          an administrator. You agree to, at all times, keep all information and materials of a
          User of a confidential or proprietary nature or not known to the public, in any format,
          strictly confidential, and under no circumstances disclose such information to any third
          party, nor duplicate, publish, excerpt, or distribute such information in any way;
        </li>
        <li>
          You agree not to post or upload to the Application any materials or content without the
          express permission and authorization of all individuals appearing in such materials or
          content. You agree to obtain all necessary permissions and releases for all individuals
          appearing in any materials or content you post or upload to the Application; and
        </li>
        <li>
          You may create and enforce rules for the Users you moderate, provided that such rules do
          not conflict with these Terms.
        </li>
      </ol>
    </section>

    {/* 12. Updates */}
    <section>
      <h2>12. Updates</h2>
      <p>
        From time to time, we may create updated versions of the Application. Updates may be
        automatically transmitted to you and you accept such updates according to these Terms.
        Unless explicitly stated otherwise, all updates will be subject to these Terms, including
        any amendments to these Terms.
      </p>
    </section>

    {/* 13. Additional Terms */}
    <section>
      <h2>13. Additional Terms</h2>
      <p>
        <b></b>Because we offer a variety of Content and Services, you may be asked to agree to
        additional terms before using or accessing a specific service offered by us
        (&quot;Additional Terms&quot;). To the extent any Additional Terms conflict with these
        Terms, the Additional Terms govern with respect to your use of the corresponding service.
      </p>
    </section>

    {/* 14. Rescue X Certification */}
    <section>
      <h2>14. Rescue X Certification</h2>
      <p>
        We may offer certain online courses through the Application. These courses are designed for
        educational and resource purposes only. We offer no authorization or certification for
        completion of online courses. Rescue X Training and Consulting, LLC only provides official
        certification through in person courses. <br />
      </p>
    </section>

    {/* 15. Digital Millennium Copyright Act */}
    <section>
      <h2>15. Digital Millennium Copyright Act</h2>
      <p>
        We comply with all copyright laws including the Digital Millennium Copyright Act
        (&quot;DMCA&quot;). If you believe the Application, or any portion thereof, violates any
        copyright that you own or control, submit notification to us at the address provided in the
        &quot;Contact&quot; section that includes:
      </p>
      <ol className="list-[lower-alpha]">
        <li>
          A physical or electronic signature of the copyright owner or a person authorized to act
          on behalf of the owner of the exclusive right that is allegedly infringed;
        </li>
        <li>
          Identification of the copyrighted work claimed to have been infringed, or, if multiple
          copyrighted works are covered by a single notification, a representative list of such
          works claimed to have been infringed;
        </li>
        <li>
          Identification of the material that is claimed to be infringing or to be the subject of
          infringing activity and that is to be removed or access to which is to be disabled;
        </li>
        <li>
          Information reasonably sufficient to permit us to contact you, such as an address,
          telephone number, and email address at which you may be contacted;
        </li>
        <li>
          A statement that you have a good faith belief that use of the material in the manner
          complained of is not authorized by you, your agent, or the law; and
        </li>
        <li>
          A statement that the information in the notification is accurate and, under penalty of
          perjury, that you are authorized to act on behalf of the owner of the exclusive right
          that is allegedly infringed.
        </li>
      </ol>

      <p>
        If we remove your content in response to a copyright or trademark notice, we will notify
        you of such action. If you believe your content was wrongly removed due to a mistake or
        misidentification in a copyright notice, you can send a counter notification in response to
        our notification of removal. Please see 17 U.S.C. § 512(g)(3) for the requirements of a
        proper counter notification.
      </p>
    </section>

    {/* 16. Third Party Apps and Hosting */}
    <section>
      <h2>16. Third Party Apps and Hosting</h2>
      <p>
        The Application may contain (or may send you through or to) links to third-party websites,
        third-party applications, and/or software (&quot;Third Party Apps&quot;). We are not
        responsible for such Third Party Apps or for any content posted on, available through, or
        installed from such Third Party Apps. Inclusion of, linking to, or permitting the use or
        installation of such Third Party Apps does not imply approval or endorsement thereof by us.
        We shall have no responsibility or liability whatsoever for any Third Party Apps accessed
        through use of the Application. Your use and interaction with Third Party Apps may be
        subject to separate terms and conditions of such Third Party Apps. If you access Third
        Party Apps, you must comply with the terms and conditions that apply. The Application may
        also contain sponsored third party content or advertisements. The type, degree, and
        targeting of advertisements are subject to change, and you acknowledge and agree that we
        may place advertisements in connection with the display of any Content, Services, or
        information on the Application, including User Content. You, the user, must comply with any
        and all applicable third-party terms of agreement, including those of the Distributor, when
        using or downloading the Application.
      </p>
    </section>

    {/* 17. Privacy Policy */}
    <section>
      <h2>17. Privacy Policy</h2>
      <p>
        <b></b>The information we obtain through your use of the Application is subject to our{' '}
        <Link to="/privacy-policy/" reloadDocument>
          <u>Privacy Policy</u>
        </Link>{' '}
        which is specifically incorporated by reference into these Terms.
      </p>
    </section>

    {/* 18. Termination */}
    <section>
      <h2>18. Termination</h2>
      <p>
        You agree that we may, at our sole discretion, terminate or suspend your access to all or
        part of the Application, with or without notice and for any reason, including breach of
        these Terms. Any suspected fraudulent, abusive, or illegal activity may be grounds for
        barring your access to this Application and reporting you to the proper authorities. You
        may terminate this Agreement only by deleting the Application and all copies thereof from
        your devices. Upon any termination of these Terms, you shall cease all use of the
        Application and delete all copies of the application from your devices. Any part of these
        Terms intended to apply following termination will so apply.
      </p>
    </section>

    {/* 19. Disclaimer of Warranty */}
    <section>
      <h2>19. Disclaimer of Warranty</h2>
      <p>
        The Application and all associated Services and Content are provided &quot;as is&quot;,
        with all faults and without warranty of any kind. We hereby disclaim all warranties with
        respect to the Application and Services either express, implied, or statutory, including
        but not limited to the implied warranties of merchantability, satisfactory quality, fitness
        for a particular purpose, accuracy, quiet enjoyment, and non-infringement of third party
        rights. We do not warrant, guarantee, or make any representations that the Application or
        Services are reliable, secure, or accurate, that they will meet your needs or requirements,
        that the Application or Services will be available at any particular time or location, that
        any defects or errors will be corrected, or that the Application or Services will be free
        of viruses or other harmful components. We further do not guarantee the accuracy of
        information provided through online courses hosted on the Application and will not be
        liable for any third-party claims or losses. You use the Application at your own risk. No
        oral or written communications from us shall create a warranty or in any way increase the
        scope of these Terms and you may not rely on any such communications. Some jurisdictions do
        not allow the exclusion or limitation of certain warranties or consumer rights so some
        exclusions or limitations may not apply to you but they shall apply to the maximum extent
        permitted by law.
      </p>
    </section>

    {/* 20. Limitation of Liability */}
    <section>
      <h2>20. Limitation of Liability</h2>
      <p>
        You hereby agree that we, along with our subsidiaries, affiliates, and assigns, and each of
        their directors, officers, agents, contractors, partners, and employees, shall not be
        liable to you or any third party for any indirect, special, consequential, or incidental
        damages including but not limited to damages for loss of funds or property, business
        interruption, loss of business opportunity, loss of data, injury, death, or any other
        hardship, damages, or losses arising out of or related to: the use or inability to use the
        Application or Services, however caused; unauthorized or accidental access to or alteration
        of data; statements or conduct of any third party; or any matter relating to the use of the
        Application or Services; and even if we have been advised of the possibility of such
        damages. Some jurisdictions do not allow the exclusion or limitation of certain remedies or
        damages so some exclusions and limitations may not apply to you but they shall apply to the
        maximum extent permitted by law.
      </p>
    </section>

    {/* 21. Indemnification */}
    <section>
      <h2>21. Indemnification</h2>
      <p>
        You hereby indemnify and hold us, our subsidiaries, affiliates, and assigns, and each of
        their directors, officers, agents, contractors, partners, and employees, harmless from and
        against any loss, liability, claim, demand, damages, costs, and expenses, including
        reasonable attorneys’ fees, arising out of these Terms or in connection with any use of the
        Application or Services including, but not limited to, any damages, losses, or liabilities
        whatsoever with respect to or arising from the possession, use, or operation of the
        Application or Services.
      </p>
    </section>

    {/* 22. Legal Compliance */}
    <section>
      <h2>22. Legal Compliance</h2>
      <p>
        You represent and warrant that (a) you are not located in a country that is subject to a
        United States government embargo, or that has been designated by the United States
        government as &quot;terrorist supporting&quot; country; and (b) you are not listed on any
        United States government list of prohibited or restricted parties. <br />
      </p>
    </section>

    {/* 23. Third Party Beneficiary */}
    <section>
      <h2>23. Third Party Beneficiary</h2>
      <p>
        Distributor and its subsidiaries may be third-party beneficiaries under these Terms, and
        such third-party beneficiaries will have the right to enforce these Terms against you.{' '}
      </p>
    </section>

    {/* 24. Severability */}
    <section>
      <h2>24. Severability</h2>
      <p>
        <b></b>If any provision of these Terms is found unenforceable or illegal, we may reform
        such provision to make it enforceable and legal or such provision may be severed and in
        either case these Terms with such provision reformed or severed will remain in full force
        and effect to the fullest extent permitted by law. Our failure to enforce any part or
        portion of these Terms shall not be considered a waiver.
      </p>
    </section>

    {/* 25. Dispute Resolution */}
    <section>
      <h2>25. Dispute Resolution</h2>
      <p>
        We hope to address all of your claims through good faith negotiation and ask that you
        contact us at the address listed in the Contact section with any issues you may have
        regarding the Application or these Terms. Any dispute, controversy, or claim arising out of
        or related to these Terms, or the breach thereof, which cannot be resolved by good faith
        negotiation between the parties shall be settled by binding arbitration by the American
        Arbitration Association in accordance with its Commercial Arbitration Rules in Denver,
        Colorado. Any resulting resolution may include the award of related fees and costs,
        including reasonable attorneys&apos; fees, and may be entered in any court of competent
        jurisdiction.
      </p>
    </section>

    {/* 26. Controlling Law */}
    <section>
      <h2>26. Controlling Law</h2>
      <p>
        These Terms shall be governed by the laws of the State of Colorado and of the United
        States. Subject to the arbitration requirement above, all claims, legal proceedings, or
        litigation arising in connection with the Application or Services will be brought solely in
        the courts located in Denver, Colorado and you consent to the exclusive jurisdiction of and
        venue in such courts and waive any objection as to inconvenient forum.
      </p>
    </section>

    {/* 27. Entire Agreement */}
    <section>
      <h2>27. Entire Agreement</h2>
      <p>
        These Terms constitute the entire agreement between you and us relating to the subject
        matter hereof and supersede all prior understandings, promises, and undertakings, if any,
        made orally or in writing with respect to the subject matter hereof. You may not assign any
        of these Terms. We may assign all or any portion of these Terms in our sole discretion. No
        modification, amendment, waiver, termination, or discharge of any portion of these Terms
        shall be binding unless executed and confirmed in writing by us. <br />
      </p>
    </section>

    {/* 28. Contact */}
    <section>
      <h2>28. Contact</h2>
      <p>If you have any questions regarding these Terms, please contact:</p>
      <address>
        <b>Rescue X Training and Consulting, LLC</b>
        <br />
        1312 17th Street #863
        <br />
        Denver, CO 80202
        <br />
        📧 <a href="mailto:info@rescuextraining.com">info@rescuextraining.com</a>
      </address>
    </section>
  </SharedLegalLayout>
)

export default TermsOfUse
